import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';
import { runLoading } from '../../../components/loading';

export interface CityRow { id: string; cityName: string | null; stateId: string | null; state: string | null; }
export interface StateOption { id: string; shortName: string; longName: string | null; countryId: string | null; }
export interface AreaRow { id: string; areaName: string | null; }
export interface CountryOption { id: string; name: string; }
/** A pending Area on a not-yet-saved City (in-memory until the parent is saved).
 *  areaId set = an existing area to link; absent = a brand-new area to create. */
export interface StagedArea { key: string; name: string; areaId?: string; }
/** A pending City on a not-yet-saved State (in-memory until the parent is saved).
 *  cityId set = an existing city to link; absent = a brand-new city to create.
 *  `areas` are staged on a brand-new city, committed after the city is created. */
export interface StagedCity { key: string; name: string; cityId?: string; areas?: StagedArea[]; }
/** A pending State on a not-yet-saved Country (in-memory until the parent is saved).
 *  stateId set = an existing state to link; absent = a brand-new state to create.
 *  `cities` are staged on a brand-new state, committed after the state is created. */
export interface StagedState { key: string; name: string; shortName?: string | null; stateId?: string; cities?: StagedCity[]; }

const CITY_KEY = ['sm-cities'];
const STATE_KEY = ['sm-states'];
const areaKey = (cityId: string) => ['sm-city-areas', cityId];
const unlinkedKey = (cityId: string) => ['sm-city-areas-unlinked', cityId];

export function useCities() {
  return useQuery({
    queryKey: CITY_KEY,
    queryFn: async () => (await api.get<{ data: CityRow[] }>('/api/v1/cities')).data.data,
  });
}

export function useCityStates() {
  return useQuery({
    queryKey: STATE_KEY,
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get<{ data: StateOption[] }>('/api/v1/cities/states')).data.data,
  });
}

export function useSaveCity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; cityName: string; stateId: string | null }) =>
      v.id
        ? api.put(`/api/v1/cities/${v.id}`, { cityName: v.cityName, stateId: v.stateId })
        : api.post('/api/v1/cities', { cityName: v.cityName, stateId: v.stateId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CITY_KEY }),
  });
}

export function useDeleteCity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      runLoading(api.delete(`/api/v1/cities/${id}`), { blocking: true, label: 'Deleting…' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CITY_KEY }),
  });
}

// ── Area child collection (City detail ▸ Area, many-to-many) ────────────────
export function useCityAreas(cityId: string | undefined) {
  return useQuery({
    queryKey: areaKey(cityId ?? ''),
    enabled: !!cityId,
    queryFn: async () => (await api.get<{ data: AreaRow[] }>(`/api/v1/cities/${cityId}/areas`)).data.data,
  });
}

export function useUnlinkedAreas(cityId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: unlinkedKey(cityId ?? ''),
    enabled: !!cityId && enabled,
    queryFn: async () => (await api.get<{ data: AreaRow[] }>(`/api/v1/cities/${cityId}/areas/unlinked`)).data.data,
  });
}

/** All active areas — the Link picker source for a brand-new (unsaved) city. */
export function useAllAreas(enabled: boolean) {
  return useQuery({
    queryKey: ['sm-all-areas'],
    enabled,
    queryFn: async () => (await api.get<{ data: AreaRow[] }>('/api/v1/cities/areas/all')).data.data,
  });
}

/** Create a STANDALONE area (no city link) — the grid "New" on a brand-new,
 *  unsaved city. Persists to dbo.Area immediately (GoLive parity), so the area is
 *  a real master row; the City↔Area link is staged until the city is saved. */
export function useCreateStandaloneArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (areaName: string) =>
      (await api.post<{ data: { id: string } }>('/api/v1/cities/areas', { areaName })).data.data.id,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-all-areas'] }),
  });
}

/** Rename a standalone area (edit from a brand-new city's Area grid). */
export function useUpdateStandaloneArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { areaId: string; areaName: string }) =>
      api.put(`/api/v1/cities/areas/${v.areaId}`, { areaName: v.areaName }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sm-all-areas'] }),
  });
}

/** Bundle the area-collection invalidations after a New/Edit/Link/Unlink. */
function useAreaInvalidate(cityId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    // The Link picker is sourced from the master 'sm-all-areas' list, so refresh it
    // too (a rename must show through in the picker as well as the city grid).
    qc.invalidateQueries({ queryKey: ['sm-all-areas'] });
    if (!cityId) return;
    qc.invalidateQueries({ queryKey: areaKey(cityId) });
    qc.invalidateQueries({ queryKey: unlinkedKey(cityId) });
  };
}

export function useCreateArea(cityId: string | undefined) {
  const invalidate = useAreaInvalidate(cityId);
  return useMutation({
    mutationFn: async (areaName: string) => api.post(`/api/v1/cities/${cityId}/areas`, { areaName }),
    onSuccess: invalidate,
  });
}

export function useUpdateArea(cityId: string | undefined) {
  const invalidate = useAreaInvalidate(cityId);
  return useMutation({
    mutationFn: async (v: { areaId: string; areaName: string }) =>
      api.put(`/api/v1/cities/${cityId}/areas/${v.areaId}`, { areaName: v.areaName }),
    onSuccess: invalidate,
  });
}

export function useLinkArea(cityId: string | undefined) {
  const invalidate = useAreaInvalidate(cityId);
  return useMutation({
    mutationFn: async (areaId: string) => api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId }),
    onSuccess: invalidate,
  });
}

export function useUnlinkArea(cityId: string | undefined) {
  const invalidate = useAreaInvalidate(cityId);
  return useMutation({
    mutationFn: async (areaId: string) =>
      runLoading(api.delete(`/api/v1/cities/${cityId}/areas/${areaId}/link`), { blocking: true, label: 'Removing…' }),
    onSuccess: invalidate,
  });
}

// ── Inline State create/edit (the State lookup pencil) ──────────────────────
/** Country options for the inline State editor (reuses the System Management endpoint). */
export function useStateCountryOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['sm-state-country-options'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get<{ data: CountryOption[] }>('/api/v1/system/states/countries')).data.data,
  });
}

/** Create (id absent) or update a State via the System Management endpoint; refresh the City dropdown. */
export function useSaveCityState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; longName: string; shortName: string | null; countryId: string | null }) => {
      const body = { longName: v.longName, shortName: v.shortName, countryId: v.countryId };
      return v.id ? api.put(`/api/v1/system/states/${v.id}`, body) : api.post('/api/v1/system/states', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STATE_KEY });
      // Mirror of systemMasters.useSaveState — keep BOTH State caches in sync.
      qc.invalidateQueries({ queryKey: ['sm-states-full'] });
      qc.invalidateQueries({ queryKey: CITY_KEY });
    },
  });
}

/** Pull a human-readable message out of an axios error (server validation etc.). */
export function errMessage(e: unknown, fallback = 'Something went wrong.'): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? fallback;
}
